import { useEffect, useState, RefObject } from 'react';
import { ContainerSize, GridConfig } from './constants';

export const useContainerSize = (containerRef: RefObject<HTMLDivElement>) => {
  const [containerSize, setContainerSize] = useState<ContainerSize>({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    const updateSize = () => {
      if (!containerRef.current) {
        return;
      }

      const nextSize = {
        width: containerRef.current.offsetWidth,
        height: containerRef.current.offsetHeight,
      };

      setContainerSize((prev) =>
        prev.width === nextSize.width && prev.height === nextSize.height
          ? prev
          : nextSize,
      );
    };

    updateSize();

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      resizeObserver = new ResizeObserver(() => updateSize());
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener('resize', updateSize);
    return () => {
      window.removeEventListener('resize', updateSize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [containerRef]);

  return containerSize;
};

export const useAutoScrollToWell = ({
  gridConfig,
  scrollContainerRef,
  wellNumber,
  plateRows,
  plateColumns,
}: {
  gridConfig: GridConfig | null;
  scrollContainerRef: RefObject<HTMLDivElement>;
  wellNumber?: number;
  plateRows: number;
  plateColumns: number;
}) => {
  useEffect(() => {
    if (!gridConfig || !scrollContainerRef.current || !wellNumber) {
      return;
    }

    const { cellSize, wellSize } = gridConfig;

    const wellIndex = wellNumber - 1;
    const row = Math.floor(wellIndex / plateColumns);
    const col = wellIndex % plateColumns;

    if (row < 0 || row >= plateRows || col < 0 || col >= plateColumns) {
      return;
    }

    const wellX = col * cellSize;
    const wellY = row * cellSize;
    const wellCenterX = wellX + wellSize / 2;
    const wellCenterY = wellY + wellSize / 2;

    const container = scrollContainerRef.current;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    const scrollLeft = wellCenterX - containerWidth / 2;
    const scrollTop = wellCenterY - containerHeight / 2;

    container.scrollTo({
      left: Math.max(0, scrollLeft),
      top: Math.max(0, scrollTop),
      behavior: 'smooth',
    });
  }, [gridConfig, scrollContainerRef, wellNumber, plateColumns, plateRows]);
};
